import React, { useState, createContext, useContext, useEffect } from 'react';
import { IAuctionItem } from 'src/pages/CatalogPage/CatalogPage.i';
import styled, { StyledComponent } from 'styled-components';
import { SocketContext } from 'src/lib/SocketContext';
import { getNextPrice } from '@riac/core';
import { useHistory, useLocation } from 'react-router-dom';
import { useFetch } from 'src/lib/FetchContext';
import { ValidateBidderModal } from 'src/components/Bidding/ValidateBidderModal';
import { AddCardModal } from 'src/components/Bidding/AddCardModal';
import { AddAddressModal } from 'src/components/Bidding/AddAddressModal';
import { AddPhoneModal } from 'src/components/Bidding/AddPhoneModal';
import { AccountPagesContextProvider } from 'src/lib/AccountPagesContext';
import { ConfirmBidModal } from 'src/components/Bidding/ConfirmBidModal';
import { AccountHeaderContext } from 'src/lib/AccountHeaderContext';
import { createTopplePixel } from 'src/lib/createTopplePixel';

interface IBiddingContext{
  placeTimedAuctionBid(item:IAuctionItem, amount?:number):Promise<IBidResponse>;
  getMessageForLot(lotNumber:number):JSX.Element;
  setMessageForLot(lotNumber:number, type:string, message:string):void;
  checkEnabled(): Promise<boolean>;
  auctionTypeId: number;
}

interface IBidResponse {
  success: boolean;
  errorMessage?: string;
}

type ValidateBidderResponse = {
  isAgreed:number,
  orderId:string,
}

export const BidResponseMessage: StyledComponent<'div', any> = styled.div`
    position: absolute;
    width: 100%;
    padding: .5em;
    z-index: 100;
    background-color: white;
    color: black;
    border: 1px solid black;
    &.error {
        background-color: rgba(120,0,0,1);
        color: rgba(255,255,255,0.85);
    }
`;

export const CloseMessage:StyledComponent<'a', any> = styled.a`
  position: absolute;
    top: 0;
    right: .5em;
    color: rgba(255,255,255,0.85);
`;
export const BiddingContext = createContext<Partial<IBiddingContext>>({});
export const NotEnabledMessage = 'You are not approved to bid in RIAC On Time, please contact customer '
  + 'support if you believe this is in error.';

export const BiddingContextProvider = ({ children, auctionId, auctionTypeId }):JSX.Element => {
  const { fetch } = useFetch();
  // const { refreshTimedLots: catalogRefreshTimedLots } = useContext(CatalogContext);
  // const { refreshTimedLots: detailRefreshTimedLots } = useContext(DetailPageContext);
  const [messages, setMessages] = useState({});
  const { socket } = useContext(SocketContext);
  const [agreed, setAgreed] = useState(0);
  const [enabled, setEnabled] = useState<boolean>(null);
  const [biddingModal, setBiddingModal] = useState<JSX.Element>(null);
  const history = useHistory();
  const location = useLocation();
  const { updateHeaderCounts } = useContext(AccountHeaderContext);
  const checkAgreed = async ():Promise<number> => {
    if(agreed && agreed > Math.floor(Date.now() / 1000)) {
      return agreed;
    }
    const agreedString = sessionStorage.getItem(`timed_agreed_${auctionId}`);
    let agreedRes:number;
    if(agreedString) {
      agreedRes = parseInt(agreedString);
    }
    if(!agreedRes) {
      try {
        const results = await (await fetch('/api/timed-auction/check-terms', {
          credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ auctionId: auctionId }),
        })).json();
        if(results.success) {
          agreedRes = Math.floor(Date.now() / 1000) + (60 * 60 * 5);
          sessionStorage.setItem(`timed_agreed_${auctionId}`, `${agreedRes}`);
        }
      } catch (e) {
        agreedRes = 0;
      }
    }
    setAgreed(agreedRes);
    return agreedRes;
  };
  const checkEnabled = async ():Promise<boolean> => {
    if(enabled !== null) {
      return enabled;
    }
    try {
      const enabledResults = await (await fetch('/api/timed-auction/check-enabled', {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      })).json();
      if(enabledResults?.success) {
        setEnabled(true);
        return true;
      }
    } catch (err) {
      console.log('Could not fetch enabled status', err);
    }
    setEnabled(false);
    return false;
  };

  const validateBidder = async ():Promise<ValidateBidderResponse> => {
    let isAgreed = 0;
    let orderId = null;
    try {
      const validateResults = await (await fetch('/api/timed-auction/validate-bidder', {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ auctionId: auctionId }),
      })).json();

      if(!validateResults.success) {
        console.error(
          'BiddingContext:ValidateBidder - fetch success false',
          { err: { message: validateResults.error },
          });
        return { isAgreed, orderId };
      }

      if(!validateResults.cards) {
        const cardAdded = await new Promise((resolve) => {
          const modal = (<AddCardModal
            onConfirm={() => resolve(true)}
            onReject={() => resolve(false)}
          />);
          setBiddingModal(modal);
        });
        if(!cardAdded) {
          setBiddingModal(null);
          return { isAgreed, orderId };
        }
      }

      if(!validateResults.addresses) {
        const addressAdded = await new Promise((resolve) => {
          const modal = (<AddAddressModal
            onConfirm={() => resolve(true)}
            onReject={() => resolve(false)}
          />);
          setBiddingModal(modal);
        });
        if(!addressAdded) {
          setBiddingModal(null);
          return { isAgreed, orderId };
        }
        return await validateBidder();
      }
      if(!validateResults.phones) {
        const phoneAdded = await new Promise((resolve) => {
          const modal = (<AddPhoneModal
            onConfirm={() => resolve(true)}
            onReject={() => resolve(false)}
          />);
          setBiddingModal(modal);
        });
        if(!phoneAdded) {
          setBiddingModal(null);
          return { isAgreed, orderId };
        }
      }

      const name = await new Promise((resolve) => {
        const modal = (<ValidateBidderModal
          stateNoticeText={validateResults.stateNotices}
          onConfirm={(name:string) => {resolve(name);}}
          onReject={()=>{resolve(false);}}
        />);
        setBiddingModal(modal);
      });

      if(!name) {
        console.error('BiddingContext:ValidateBidder - incomplete validate modal');
        setBiddingModal(null);
        return { isAgreed, orderId };
      }


      setBiddingModal(null);
      const orderResult = await (await fetch('/api/timed-auction/place-order', {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ noticeStateChecked: validateResults.stateNotices.length, signature: name }),
      })).json();

      if(orderResult.success) {
        orderId = orderResult.orderId;
      }
      isAgreed = Math.floor(Date.now() / 1000) + (60 * 10);
      return { isAgreed, orderId };
    } catch (err) {
      console.error('BiddingContext:ValidateBidder', { err });
      return { isAgreed, orderId };
    }
  };

  useEffect(() => {
    if(auctionTypeId && auctionTypeId === TIMED_AUCTION_TYPE_ID) {
      checkEnabled().then();
    }
  }, [auctionTypeId]);

  const setMessageForLot = (lotNumber:number, type:string = null, message:string = null):void => {
    const newMessages = { ...messages };
    if(type === null && message === null) {
      delete newMessages[lotNumber];
      setMessages(newMessages);
      return;
    }
    if(!message) {
      message = 'Unknown error';
    }
    newMessages[lotNumber] = {
      type: type,
      message: message,
    };
    setMessages(newMessages);
  };

  const getMessageForLot = (lotNumber:number):JSX.Element => {
    const message = messages[lotNumber];
    if(typeof message !== 'undefined') {
      return (<BidResponseMessage className={message.type}>
        {message.message}
        <CloseMessage onClick={()=>(setMessageForLot(lotNumber, null, null))}>&times;</CloseMessage>
      </BidResponseMessage>);
    }
    return null;
  };

  const createConfirm = (message:string, item:IAuctionItem, amount:number):Promise<boolean> => {
    return new Promise((resolve) => {
      const modal = (<ConfirmBidModal
        onConfirm={(confirm:boolean) => {
          setBiddingModal(null);
          resolve(confirm);
        }}
        onReject={() => {
          setBiddingModal(null);
          resolve(false);
        }}
        item={item}
        amount={amount}
        message={message}
      />
      );
      setBiddingModal(modal);
    });
  };

  const placeTimedAuctionBid = async (
    item:IAuctionItem,
    amount = null,
    skipConfirm = false,
    isIncrement = false
  ):Promise<IBidResponse> => {
    if(!enabled) {
      setMessageForLot(
        item.LotNumber,
        'error',
        NotEnabledMessage
      );
      return { success: false, errorMessage: NotEnabledMessage };
    }
    let isAgreed = await checkAgreed();
    let orderId = null;
    // Clear our lot messages
    setMessageForLot(item.LotNumber);
    if(amount === null) {
      amount = item.StartingBid;
      if(item.CurrentBid) {
        amount = getNextPrice(item.CurrentBid);
      }
      isIncrement = true;
    }
    if(!skipConfirm) { // Build and send confirmation
      const confirm = await createConfirm(`Are you sure you wish to place a bid for $${amount}?`, item, amount);
      if(!confirm) {
        const errMessage = 'Bid canceled.';
        setMessageForLot(item.LotNumber, 'error', errMessage);
        return { success: false, errorMessage: errMessage };
      }
    }

    // If we haven't agreed to the T&Cs and verified we have done all we need to do to bid,
    if(!isAgreed) {
      ({ isAgreed, orderId } = await validateBidder());

      if(!isAgreed || !orderId) {
        const message = 'Your bidding status could not be validated, bid canceled';
        setMessageForLot(item.LotNumber, 'error', message);
        return { success: false, errorMessage: message };
      }
    }

    // Establish our socketId if we have one.
    let socketId = null;
    if(!socket.connected) {
      const connResult = await socket.connect();
      socketId = connResult.id;
    }
    if(!socketId && socket.connected) {
      socketId = socket.id;
    }

    const bidReq = await fetch('/api/timed-auction/bid', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        item: item,
        bidAmount: amount,
        socketId: socketId,
        isIncrement: isIncrement,
        verified: true,
        orderId: orderId,
      }),
    });
    const bidResponse = await bidReq.json();
    if(!bidResponse.success) {
      // handle error.
      setMessageForLot(item.LotNumber, 'error', bidResponse.errorMessage);
    }
    createTopplePixel('bidSubmission', amount);
    updateHeaderCounts();
    return bidResponse;
  };

  return (<BiddingContext.Provider value={{
    placeTimedAuctionBid: placeTimedAuctionBid,
    getMessageForLot: getMessageForLot,
    setMessageForLot: setMessageForLot,
    checkEnabled: checkEnabled,
    auctionTypeId: auctionTypeId,
  }}>
    <AccountPagesContextProvider>
      {biddingModal}
      {children}
    </AccountPagesContextProvider>
  </BiddingContext.Provider>);
};
